import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query"; 
import { 
  Container, 
  Typography, 
  Box, 
  Divider, 
  Link,
  CircularProgress 
} from "@mui/material";
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import axiosClient from "axiosClient";
import BlueLine from "./BlueLine";
import newsDetailsBackground from '../../assets/centrum_assets/design/stations/stationBG.png'
import DOMPurify from 'dompurify'; // 1. IMPORT DOMPURIFY

// Components
import NewsSidebar from "../components/NewsSidebar"; 
import { Helmet } from "react-helmet-async";

const NewsDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const initialNews = location.state?.news;
  const initialList = location.state?.newsData || []; 
  const newsId = initialNews?.id;

  const { data: news, isLoading: newsLoading } = useQuery({
    queryKey: ['news', newsId],
    queryFn: async () => {
      if (!newsId) return null;
      const res = await axiosClient.get(`/article-posts/${newsId}`);
      return res.data.data; 
    },
    enabled: !!newsId,
    initialData: initialNews, 
  });

  const { data: newsData = [], isLoading: listLoading } = useQuery({
    queryKey: ['news'], 
    queryFn: async () => {
      const res = await axiosClient.get('/article-posts');
      return res.data.data || [];
    },
    initialData: initialList.length > 0 ? initialList : undefined
  });

  const loading = newsLoading || listLoading;
  const [imgLoaded, setImgLoaded] = useState(false);

  const shareUrl = window.location.href;
  const handleFacebookClick = () => {
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(fbUrl, "_blank", "width=600,height=400");
  };

  const handleInstagramClick = () => {
    window.open("https://www.instagram.com/", "_blank");
  };

  const handleNewsChange = (targetNews) => {
    if (targetNews) {
        setImgLoaded(false);
        navigate("/news_details", {
            state: {
                news: targetNews,
                newsData: newsData
            },
            replace: true
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading && !news) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress sx={{ color: "#009fb6" }} />
      </Box>
    );
  }

  if (!news) {
    return (
      <Typography variant="h5" mt={10} textAlign="center">
        No news data received.
      </Typography>
    );
  }
  
  const displayImage = news.mainImage || news.cardPic;

  return (
    <> 
    <Helmet>
        <meta property="og:title" content={news.cardTitle} />
        <meta property="og:image" content={displayImage} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="article" />     
    </Helmet>
    <Box sx={{ backgroundImage: `url(${newsDetailsBackground})` }}>
      <Container sx={{ py: 6 }}>
        <Typography
          sx={{ mb: 2 }}
          fontSize={25}
          fontWeight={800}
          color="#0a1a3b" 
        >
          {news.cardTitle} 
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 4, 
            flexDirection: { xs: "column", md: "row" },
            mb: 3,
            alignItems: "flex-start", 
          }}
        >
          {/* IMAGE CONTAINER */}
          <Box
            sx={{
              flex: 3,
              height: { xs: 200, sm: 300, md: 500 }, 
              position: "relative",
              boxShadow: 4,
              overflow: "hidden",
              backgroundColor: "#f5f5f5", 
              width: "100%"
            }}
          >
            {!imgLoaded && (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center", zIndex: 3 }}>
                <CircularProgress size={40} sx={{ color: "#0a1a3b" }} />
              </Box>
            )}

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${displayImage})`, 
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "blur(6px)",
                transform: "scale(1.2)",
                zIndex: 1,
                opacity: imgLoaded ? 1 : 0, 
                transition: "opacity 0.8s ease-in-out",
              }}
            />

            <Box
              component="img"
              src={displayImage} 
              alt={news.cardTitle} 
              onLoad={() => setImgLoaded(true)} 
              sx={{
                height: "100%",
                width: "100%",
                objectFit: "contain",
                position: "relative",
                zIndex: 2,
                p: 0,
                opacity: imgLoaded ? 1 : 0, 
                transition: "opacity 0.8s ease-in-out",
              }}
            />
          </Box>

          {/* --- SIDEBAR (Desktop) --- */}
          <NewsSidebar 
            list={newsData}
            activeItem={news}
            onNavigate={handleNewsChange}
            sx={{ 
              flex: 1,
              minWidth: { md: "300px" },
              display: { xs: "none", md: "flex" } 
            }}
          />
        </Box>

        {/* FULL ARTICLE DESCRIPTION */}
       <BlueLine height="15%">
          <Typography
            component="div" // Prevents <p> inside <p> warnings
            sx={{
              maxWidth: { xs: "100%", md: "66%", lg: '70%' },
              textAlign: 'justify',
              mt: 0,
              
              //(1.4rem for desktop, 1.7rem for mobile):
              '& p': { 
                  margin: 0, 
                  marginBottom: '1em', 
                  fontSize: { md: '1.4rem', xs: '1.7rem' }, 
                  fontWeight: 400, 
                  lineHeight: 1.7, 
                  color: '#444' 
              },
              '& ul, & ol': { 
                  paddingLeft: 3, 
                  marginBottom: '1em', 
                  color: '#444', 
                  fontSize: { md: '1.4rem', xs: '1.7rem' } 
              },
              
              '& h1, & h2, & h3': { color: '#0a1a3b', fontWeight: 'bold', mt: 4, mb: 2 },
              '& img': { maxWidth: '100%', height: 'auto', borderRadius: '8px', my: 3 }
            }}
            dangerouslySetInnerHTML={{ 
              __html: DOMPurify.sanitize(news.article || news.cardDesc || '<p>No description available.</p>') 
            }}
          />
        </BlueLine>
          
        {/* SHARE OPENING */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 4, mt: 8, justifyContent: { xs: "center", md: "flex-start" } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 2, color: "#0a1a3b", fontSize: '1.25rem' }}>
            SHARE ARTICLE
          </Typography>

          <FacebookIcon onClick={handleFacebookClick} fontSize="large" sx={{ cursor: "pointer", color: "#0a1a3b", "&:hover": { color: "#00b0f0" } }} />
          <InstagramIcon onClick={handleInstagramClick} fontSize="large" sx={{ cursor: "pointer", color: "#0a1a3b", "&:hover": { color: "#00b0f0" } }} />
        </Box>

        <Divider sx={{ my: 4, borderColor: "#3f3a3aff", width: "100%" }} />

        {/* --- SIDEBAR (Mobile/Tablet) --- */}
        <NewsSidebar 
            list={newsData}
            activeItem={news}
            onNavigate={handleNewsChange}
            sx={{ display: { xs: "flex", md: "none" }, mt: 2, maxHeight: 300 }}
        />
      </Container>
    </Box>
    </> 
  );
};

export default NewsDetails;